<template>
  <div class="expiry-report">
    <ReportChoices
      label="Expiring within"
      icon="ti-calendar-time"
      :choices="windowChoices"
      :active="filters.window"
      @select="filters.window = $event.key"
    />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter">
          <label>Subcategory</label>
          <FormDropdown v-model="filters.sub_category" :options="subCategoryOptions" placeholder="All subcategories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search name, code, SKU or barcode" />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!visibleRows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: statusFilter === 'expired' }"
              @click="toggleStatus('expired')">
        <span class="rp-card-icon"><i class="ti ti-calendar-x"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Expired</div>
          <div class="rp-card-value text-danger">{{ formatMoney(groups.expired.value) }}</div>
          <div class="rp-card-sub">{{ plural(groups.expired.count, 'batch') }} · {{ formatQty(groups.expired.qty) }} units</div>
        </div>
      </button>
      <button type="button" class="rp-card rp-card-action tone-orange" :class="{ selected: statusFilter === 'expiring' }"
              @click="toggleStatus('expiring')">
        <span class="rp-card-icon"><i class="ti ti-hourglass-high"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Expiring in {{ summary.window }} days</div>
          <div class="rp-card-value" style="color: #D9771E">{{ formatMoney(groups.expiring.value) }}</div>
          <div class="rp-card-sub">{{ plural(groups.expiring.count, 'batch') }} · {{ formatQty(groups.expiring.qty) }} units</div>
        </div>
      </button>
      <button type="button" class="rp-card rp-card-action tone-green" :class="{ selected: statusFilter === 'ok' }"
              @click="toggleStatus('ok')">
        <span class="rp-card-icon"><i class="ti ti-calendar-check"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Safe</div>
          <div class="rp-card-value text-success">{{ formatMoney(groups.ok.value) }}</div>
          <div class="rp-card-sub">{{ plural(groups.ok.count, 'batch') }} · {{ formatQty(groups.ok.qty) }} units</div>
        </div>
      </button>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-calendar-question"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">No Expiry Date</div>
          <div class="rp-card-value">{{ formatInt(undated.count) }}</div>
          <div class="rp-card-sub">batches · {{ formatMoney(undated.value) }}</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Stock value by expiry month</span>
          <span class="rp-legend">
            <span><i class="rp-dot out"></i>Expired</span>
            <span><i class="rp-dot" style="background: #FE9F43"></i>Within {{ summary.window }} days</span>
            <span><i class="rp-dot" style="background: #2D6CDF"></i>Later</span>
          </span>
        </div>
        <div v-show="hasTimeline" class="rp-chart"><canvas ref="timelineChart"></canvas></div>
        <p v-if="!hasTimeline" class="text-muted small mb-0">No dated batches in stock.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">At-risk value by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span class="fw-semibold">{{ formatMoney(category.value) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill danger" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">Nothing expired or expiring soon.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Items to clear first</div>
        <ul v-if="summary.at_risk_items.length" class="rp-ranked">
          <li v-for="(item, index) in summary.at_risk_items" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatQty(item.qty) }} units · {{ formatMoney(item.value) }}</small>
            </div>
            <span class="rp-badge" :class="item.expired ? 'danger' : 'warning'">
              {{ item.expired ? 'Expired' : formatDate(item.first_expiry) }}
            </span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">Nothing expired or expiring soon.</p>
      </div>
    </div>

    <!-- Table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in statusPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: statusFilter === pill.key }" @click="statusFilter = pill.key">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="fw-semibold">{{ row.item_code }}</td>
              <td>
                <div class="fw-medium">{{ row.item_name }}</div>
                <span v-if="row.sku" class="rp-sub">SKU: {{ row.sku }}</span>
              </td>
              <td>
                <template v-if="row.variant">
                  <span v-for="part in row.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ row.warehouse }}</td>
              <td>
                {{ row.batch_no || '—' }}
                <span v-if="row.lot_no && row.lot_no !== '000000'" class="rp-sub">Lot {{ row.lot_no }}</span>
              </td>
              <td class="text-nowrap">{{ formatDate(row.in_house_date) }}</td>
              <td class="text-nowrap fw-semibold">{{ formatDate(row.expiry_date) }}</td>
              <td class="text-end">
                <span class="rp-badge" :class="statusTone(row.status)">{{ daysText(row.days_left) }}</span>
              </td>
              <td class="text-end">{{ formatQty(row.qty) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small></td>
              <td class="text-end fw-semibold">{{ formatMoney(row.value) }}</td>
              <td><span class="rp-badge" :class="statusTone(row.status)">{{ row.status_label }}</span></td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-calendar-check text-success"></i>
                  <div class="fw-semibold">No batches in this view</div>
                  <small class="text-muted">Batches in stock with an expiry date appear here.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th colspan="8" class="text-end">Total ({{ plural(visibleRows.length, 'batch') }})</th>
              <th class="text-end">{{ formatQty(visibleTotals.qty) }}</th>
              <th class="text-end">{{ formatMoney(visibleTotals.value) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml, localToday } from './inventoryReportMixin';

const DEFAULT_WINDOW = 30;
const EMPTY_GROUP = { count: 0, qty: 0, value: 0 };
const EMPTY_SUMMARY = {
  window: DEFAULT_WINDOW, groups: { expired: EMPTY_GROUP, expiring: EMPTY_GROUP, ok: EMPTY_GROUP },
  undated: EMPTY_GROUP, at_risk_value: 0, timeline: [], by_category: [], at_risk_items: [],
};
const TONES = { expired: 'danger', expiring: 'warning', ok: 'success' };

export default {
  name: 'ExpiryReport',
  mixins: [inventoryReport],

  reportUrl: apiUrls.expiry_report.get,
  fileName: 'expiry_report',
  columns: [
    { key: 'item_code', label: 'Item Code' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'variant', label: 'Variant' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'batch_no', label: 'Batch' },
    { key: 'in_house_date', label: 'Received' },
    { key: 'expiry_date', label: 'Expiry Date' },
    { key: 'days_left', label: 'Days Left', class: 'text-end', numeric: true },
    { key: 'qty', label: 'Quantity', class: 'text-end', numeric: true },
    { key: 'value', label: 'Value', class: 'text-end', numeric: true },
    { key: 'status_label', label: 'Status' },
  ],

  data() {
    return {
      filters: { window: DEFAULT_WINDOW, warehouse: null, category: null, sub_category: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      statusFilter: 'all',
      sortKey: 'days_left',
      sortDir: 'asc',
      windowChoices: [7, 15, 30, 60, 90].map((days) => ({ key: days, label: `${days} days` })),
    };
  },

  computed: {
    groups() {
      return { ...EMPTY_SUMMARY.groups, ...this.summary.groups };
    },

    undated() {
      return this.summary.undated || EMPTY_GROUP;
    },

    hasActiveFilters() {
      return this.itemFiltersActive || this.filters.window !== DEFAULT_WINDOW || this.statusFilter !== 'all';
    },

    hasTimeline() {
      return this.summary.timeline.some((t) => Number(t.value));
    },

    statusPills() {
      const g = this.groups;
      return [
        { key: 'all', label: 'All batches', count: this.rows.length },
        { key: 'expired', label: 'Expired', count: g.expired.count },
        { key: 'expiring', label: 'Expiring soon', count: g.expiring.count },
        { key: 'ok', label: 'Safe', count: g.ok.count },
      ];
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category, 'value');
    },

    visibleRows() {
      const rows = this.statusFilter === 'all' ? this.rows : this.rows.filter((r) => r.status === this.statusFilter);
      return this.sortRows(rows);
    },

    visibleTotals() {
      return this.visibleRows.reduce((totals, row) => ({
        qty: totals.qty + (Number(row.qty) || 0), value: totals.value + (Number(row.value) || 0),
      }), { qty: 0, value: 0 });
    },
  },

  watch: {
    'filters.window'() { this.onFilterChange(); },
    statusFilter() { this.page = 1; },
  },

  methods: {
    extraParams() {
      return { window: this.filters.window };
    },

    resetExtraFilters() {
      this.statusFilter = 'all';
      return { window: DEFAULT_WINDOW };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(this.renderTimeline);
    },

    toggleStatus(status) {
      this.statusFilter = this.statusFilter === status ? 'all' : status;
    },

    statusTone(status) {
      return TONES[status] || 'muted';
    },

    daysText(days) {
      if (days < 0) return `${this.formatInt(-days)} days ago`;
      if (days === 0) return 'Today';
      return `${this.formatInt(days)} days`;
    },

    renderTimeline() {
      if (!this.hasTimeline) return;
      const limit = new Date(`${localToday()}T00:00:00`);
      limit.setDate(limit.getDate() + this.summary.window);
      const entries = this.summary.timeline;
      const labelOf = (key) => {
        if (key === 'expired') return 'Expired';
        if (key === 'later') return 'Later';
        const [year, month] = key.split('-');
        return new Date(Number(year), Number(month) - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
      };
      const colorOf = (key) => {
        if (key === 'expired') return '#EA5455';
        if (key === 'later') return '#9AA1A9';
        return new Date(`${key}T00:00:00`) <= limit ? '#FE9F43' : '#2D6CDF';
      };
      this.drawChart('timeline', this.$refs.timelineChart, {
        type: 'bar',
        data: {
          labels: entries.map((e) => labelOf(e.key)),
          datasets: [{
            label: 'Stock value',
            data: entries.map((e) => Number(e.value) || 0),
            backgroundColor: entries.map((e) => colorOf(e.key)),
            borderRadius: 4,
            maxBarThickness: 22,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => ` ${this.formatMoney(ctx.parsed.y)}` } },
          },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true } },
            y: { beginAtZero: true, grid: { color: '#F0F2F5' }, ticks: { font: { size: 10 } } },
          },
        },
      });
    },

    printReport() {
      const g = this.groups;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.item_code)}</td>
          <td>${escapeHtml(r.item_name)}</td>
          <td>${escapeHtml(r.variant)}</td>
          <td>${escapeHtml(r.warehouse)}</td>
          <td>${escapeHtml(r.batch_no)}</td>
          <td>${this.formatDate(r.expiry_date)}</td>
          <td class="num">${escapeHtml(this.daysText(r.days_left))}</td>
          <td class="num">${this.formatQty(r.qty)}</td>
          <td class="num">${this.formatMoney(r.value)}</td>
          <td class="${{ expired: 'out_of_stock', expiring: 'low_stock' }[r.status] || ''}">${escapeHtml(r.status_label)}</td>
        </tr>`).join('');
      const t = this.visibleTotals;
      const table = `<table><thead><tr><th>Item Code</th><th>Item Name</th><th>Variant</th><th>Warehouse</th>
          <th>Batch</th><th>Expiry Date</th><th class="num">Days Left</th><th class="num">Quantity</th>
          <th class="num">Value</th><th>Status</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="7" class="num">Total</td><td class="num">${this.formatQty(t.qty)}</td>
          <td class="num">${this.formatMoney(t.value)}</td><td></td></tr></tfoot></table>`;
      this.openPrint(
        'Expiry Report',
        [[`As on: ${this.formatDate(localToday())}`, `Expiring soon: within ${this.summary.window} days`,
          ...this.itemFiltersText()].join(' · '), `Printed ${new Date().toLocaleString()}`],
        [
          ['Expired', `${this.plural(g.expired.count, 'batch')} · ${this.formatMoney(g.expired.value)}`],
          [`Expiring in ${this.summary.window} days`, `${this.plural(g.expiring.count, 'batch')} · ${this.formatMoney(g.expiring.value)}`],
          ['Safe', `${this.plural(g.ok.count, 'batch')} · ${this.formatMoney(g.ok.value)}`],
          ['No Expiry Date', this.plural(this.undated.count, 'batch')],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="./inventoryReport.css"></style>
